import { Card, Rate, Space, Tag, Divider, Progress } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import tw from "tailwind-styled-components";
import PageTitle from "../page-header/PageHeader";
import Loader from "../loader/loader";

const DetailEmployeeSkill = () => {
	const { id } = useParams();
	const [skill, setSkill] = useState(null);
	const [loading, setLoading] = useState(true);
	const isLogged = Boolean(localStorage.getItem("isLogged"));

	useEffect(() => {
		// Get skills from localStorage
		const storedSkills = localStorage.getItem("employeeSkills");
		if (storedSkills) {
			try {
				const skills = JSON.parse(storedSkills);
				const skillId = parseInt(id);
				const foundSkill = skills.find((s) => s.id === skillId || String(s.id) === String(id));
				if (foundSkill) {
					setSkill(foundSkill);
				}
			} catch (error) {
				console.error("Error parsing skills from localStorage:", error);
			}
		}
		setLoading(false);
	}, [id]);

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	if (loading) {
		return <Loader />;
	}

	if (!skill) {
		return (
			<div>
				<PageTitle title="Back" />
				<Card className="mt-4">
					<div className="text-center">
						<h3 className="text-xl text-red-500">Employee Skill not found</h3>
						<p className="text-gray-600 mt-2">The skill with ID {id} could not be found.</p>
					</div>
				</Card>
			</div>
		);
	}

	const getProficiencyColor = (level) => {
		switch (level) {
			case "Expert":
				return "green";
			case "Advanced":
				return "blue";
			case "Intermediate":
				return "orange";
			default:
				return "default";
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "Active":
				return "green";
			case "In Progress":
				return "blue";
			case "Completed":
				return "purple";
			case "Expired":
				return "red";
			default:
				return "default";
		}
	};

	const progressToTarget = skill.targetRating > 0
		? (skill.currentRating / skill.targetRating) * 100
		: 0;

	return (
		<div>
			<PageTitle title="Back" />
			<Card className="mt-4">
				<div className="text-center mb-4">
					<h2 className="text-2xl font-semibold text-gray-600">
						Employee Skill #{skill.id}
					</h2>
					<Space className="mt-2">
						<Tag color={getStatusColor(skill.status)} style={{ fontSize: 14, padding: "4px 12px" }}>
							{skill.status || "Active"}
						</Tag>
						<Tag color={getProficiencyColor(skill.proficiencyLevel)} style={{ fontSize: 14, padding: "4px 12px" }}>
							{skill.proficiencyLevel || "Beginner"}
						</Tag>
					</Space>
				</div>
				{skill ? (
					<div className="flex justify-center">
						<div className="w-full max-w-4xl">
							<ul className="list-inside list-none border-2 border-inherit rounded px-5 py-5">
								<ListItem>
									Employee:{" "}
									<TextInside>{skill.employeeName || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Skill Category:{" "}
									<TextInside>{skill.skillCategory || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Skill Name:{" "}
									<TextInside>{skill.skillName || "N/A"}</TextInside>
								</ListItem>
								<ListItem>
									Proficiency Level:{" "}
									<TextInside>
										<Tag color={getProficiencyColor(skill.proficiencyLevel)}>
											{skill.proficiencyLevel || "Beginner"}
										</Tag>
									</TextInside>
								</ListItem>
								<ListItem>
									Current Rating:{" "}
									<TextInside>
										<Rate disabled defaultValue={skill.currentRating} allowHalf style={{ fontSize: 16 }} />
										<span className="ml-2">({skill.currentRating}/5)</span>
									</TextInside>
								</ListItem>
								{skill.targetRating > 0 && (
									<ListItem>
										Target Rating:{" "}
										<TextInside>
											<Rate disabled defaultValue={skill.targetRating} allowHalf style={{ fontSize: 16 }} />
											<span className="ml-2">({skill.targetRating}/5)</span>
										</TextInside>
									</ListItem>
								)}
								{skill.targetRating > 0 && (
									<ListItem>
										Progress to Target:{" "}
										<TextInside>
											<Progress
												percent={Math.min(100, progressToTarget)}
												status={progressToTarget >= 100 ? "success" : "active"}
												strokeColor={progressToTarget >= 100 ? "#52c41a" : "#1890ff"}
											/>
										</TextInside>
									</ListItem>
								)}
								{skill.experienceYears > 0 && (
									<ListItem>
										Experience:{" "}
										<TextInside>{skill.experienceYears} years</TextInside>
									</ListItem>
								)}
								<ListItem>
									Assessment Date:{" "}
									<TextInside>
										{skill.assessmentDate
											? dayjs(skill.assessmentDate).format("DD-MM-YYYY")
											: "N/A"}
									</TextInside>
								</ListItem>
								{skill.acquiredDate && (
									<ListItem>
										Date Acquired:{" "}
										<TextInside>
											{dayjs(skill.acquiredDate).format("DD-MM-YYYY")}
										</TextInside>
									</ListItem>
								)}

								{(skill.certificationName || skill.certificationProvider) && (
									<Divider orientation="left">Certification Details</Divider>
								)}

								{skill.certificationName && (
									<ListItem>
										Certification Name:{" "}
										<TextInside>{skill.certificationName}</TextInside>
									</ListItem>
								)}
								{skill.certificationProvider && (
									<ListItem>
										Certification Provider:{" "}
										<TextInside>{skill.certificationProvider}</TextInside>
									</ListItem>
								)}
								{skill.certificationDate && (
									<ListItem>
										Certification Date:{" "}
										<TextInside>
											{dayjs(skill.certificationDate).format("DD-MM-YYYY")}
										</TextInside>
									</ListItem>
								)}

								{skill.trainingCompleted && (
									<>
										<Divider orientation="left">Training & Development</Divider>
										<ListItem>
											Training/Course Completed:{" "}
											<TextInside>{skill.trainingCompleted}</TextInside>
										</ListItem>
									</>
								)}

								{skill.workExamples && (
									<>
										<Divider orientation="left">Work Examples</Divider>
										<ListItem>
											Projects/Work Examples:{" "}
											<TextInside>{skill.workExamples}</TextInside>
										</ListItem>
									</>
								)}

								{skill.notes && (
									<>
										<Divider orientation="left">Additional Information</Divider>
										<ListItem>
											Notes:{" "}
											<TextInside>{skill.notes}</TextInside>
										</ListItem>
									</>
								)}
							</ul>
						</div>
					</div>
				) : (
					<Loader />
				)}
			</Card>
		</div>
	);
};

const ListItem = tw.li`
	text-sm
	text-gray-600
	font-semibold
	py-2
	px-4
	bg-gray-100
	mb-1.5
	rounded
	w-full
	flex
	justify-start
`;

const TextInside = tw.p`
	ml-2
	text-sm
	text-gray-900
	flex-1
`;

export default DetailEmployeeSkill;

